import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify'

// import GmapVue from 'gmap-vue'
// Vue.use(GmapVue, {
//   load: {
//     key: 'AIzaSyDDpGdq0Cm4KSk0U7wZBGzcutjZZ4xmi7A',
//   },
// })

import Gmaps from '@/plugins/gmaps'
Vue.use(Gmaps)

import VueWobCache from './vue-wob-cache/vue-wob-cache'
import { AdtClient } from '@/wob-client/adt-client'
import { MsalAuth } from '@/auth/msal-auth'

// Backend kind for this build. Plumbed through every CI/CD workflow as
// VUE_APP_DEFAULT_PROFILE so the same git branch can produce both ADT and
// PCX builds with no code changes.
const DEFAULT_PROFILE_KIND = process.env.VUE_APP_DEFAULT_PROFILE || 'adt'

// MSAL redirect URI. Per-environment override via env var; default to the
// staging host so local dev still hits a registered redirect.
const redirectUri =
  process.env.VUE_APP_MSAL_REDIRECT_URL || 'https://adt-x.webofbuildings.com'

// Per-kind defaults used when no persisted profile is available (or the
// persisted one belongs to a different kind than the build is targeting).
const DEFAULT_PROFILES = {
  adt: {
    kind: 'adt',
    tenantId: '7acfac5a-c5e6-4f8b-8989-88611df55430',
    clientId: '2cbae6ed-b6e8-4439-ad96-99638e9e96ff',
    hostName: 'wob-klovern.api.neu.digitaltwins.azure.net',
  },
}

// Read the connection profile for the active kind. The persisted profile
// is honoured only when its kind matches; otherwise we fall back to the
// per-kind default. Legacy 'adtSettings' is migrated for ADT builds.
function loadConnectionProfile(kind) {
  const raw = localStorage.getItem('connectionProfile')
  if (raw) {
    const persisted = JSON.parse(raw)
    if (persisted.kind === kind) return persisted
  }

  if (kind === 'adt') {
    const legacyRaw = localStorage.getItem('adtSettings')
    if (legacyRaw) {
      const migrated = { kind: 'adt', ...JSON.parse(legacyRaw) }
      localStorage.setItem('connectionProfile', JSON.stringify(migrated))
      return migrated
    }
  }

  const fallback = DEFAULT_PROFILES[kind]
  if (!fallback) {
    throw new Error(`main.js: no default profile registered for kind='${kind}'`)
  }
  return { ...fallback }
}

// Select the backend triple (client/auth/applyProfile factories) for a kind.
// Today only ADT is implemented; PCX will be wired by a follow-up PR that
// adds PcxClient + AwsSigV4Auth and a 'pcx' branch here.
function backendFor(kind) {
  if (kind === 'adt') {
    return {
      clientForProfile: (p) =>
        new AdtClient({ origin: `https://${p.hostName}` }),
      authForProfile: (p) =>
        new MsalAuth({
          clientId: p.clientId,
          tenantId: p.tenantId,
          redirectUri,
        }),
      applyProfile: (client, p) => {
        client.setOrigin(`https://${p.hostName}`)
      },
    }
  }
  if (kind === 'pcx') {
    throw new Error(
      'main.js: PCX backend not yet implemented — wire PcxClient + AwsSigV4Auth in a follow-up PR.'
    )
  }
  throw new Error(`main.js: unknown VUE_APP_DEFAULT_PROFILE='${kind}'`)
}

// ?engine=next runs this profile on the next-generation cache;
// ?engine=legacy goes back. Kept in the profile. With engine=next, the
// tenantid/clientid/hostname parameters also apply at boot, so one link
// opens a backend on the new engine (0.9.x applies them later, in App.vue).
function applyEngineParams(profile) {
  const params = new URLSearchParams(window.location.search)
  const engine = params.get('engine')
  if (engine === 'next') profile.engine = 'next'
  else if (engine === 'legacy') delete profile.engine
  if (profile.engine === 'next') {
    const tenantId = params.get('tenantid')
    const clientId = params.get('clientid')
    const hostName = params.get('hostname')
    if (tenantId && clientId && hostName) {
      Object.assign(profile, { tenantId, clientId, hostName })
    }
  }
  if (engine) localStorage.setItem('connectionProfile', JSON.stringify(profile))
  return profile
}

const profile = applyEngineParams(loadConnectionProfile(DEFAULT_PROFILE_KIND))
const { clientForProfile, authForProfile, applyProfile } =
  backendFor(DEFAULT_PROFILE_KIND)

async function installCache() {
  if (profile.engine === 'next' && DEFAULT_PROFILE_KIND === 'adt') {
    // Loaded only when chosen, so 0.9.x's bundle is unchanged.
    const { default: VueWobCacheNext } = await import(
      /* webpackChunkName: "rec-cache-next" */ './vue-wob-cache-next/vue-wob-cache-next'
    )
    Vue.use(VueWobCacheNext, {
      auth: authForProfile(profile),
      // 0.9.x's client, for create, update and change of type.
      client: clientForProfile(profile),
      profile,
      saveProfile: (p) =>
        localStorage.setItem('connectionProfile', JSON.stringify(p)),
    })
    return
  }
  const client = clientForProfile(profile)
  const auth = authForProfile(profile)
  Vue.use(VueWobCache, {
    client,
    auth,
    profile,
    authForProfile,
    applyProfile,
  })
}

Vue.config.productionTip = false

installCache().then(() =>
  new Vue({
    router,
    store,
    vuetify,
    render: (h) => h(App),
  }).$mount('#app')
)
